import * as React from "react";
import type { SearchSource } from "@lupp/shared";
import type { EditorView } from "@codemirror/view";
import { renderMarkdown } from "../services/markdown";
import { copyTableToClipboard } from "../services/tableClipboard";
import { writeRichText } from "../services/clipboard";
import { mountCodeBlock } from "../services/codeMirror";
import { COPY_ICON_SVG } from "./icons";

interface MarkdownProps {
  content: string;
  /** Code-Blöcke via CodeMirror rendern. Während des Streamings aus (vermeidet Re-Mount je Token). */
  enhanceCode?: boolean;
  className?: string;
  /** Source citations: inline [n] in the text is replaced by a button for citations[n-1]. */
  citations?: SearchSource[];
}

/** Registrable domain (second-level + TLD) from a URL, e.g. www.fraunhofer.de → fraunhofer.de. */
function secondLevelDomain(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    const parts = host.split(".");
    if (parts.length <= 2) return host;
    const twoPartTld = new Set(["co.uk", "org.uk", "ac.uk", "gov.uk", "com.au", "co.jp", "com.br", "co.in", "co.za"]);
    const lastTwo = parts.slice(-2).join(".");
    return twoPartTld.has(lastTwo) ? parts.slice(-3).join(".") : lastTwo;
  } catch {
    return url;
  }
}

/** Percent-encode for a Scroll-To-Text-Fragment (also encode '-' since it's a directive delimiter). */
function encFrag(s: string): string {
  return encodeURIComponent(s).replace(/-/g, "%2D");
}

/**
 * Build a Scroll-To-Text-Fragment URL (#:~:text=…) so the browser jumps to/highlights the
 * quote. For long quotes we use the `textStart,textEnd` form (first/last few words) — far
 * more robust than requiring an exact full-sentence match.
 */
function textFragmentUrl(url: string, quote: string): string {
  const words = quote.trim().split(/\s+/);
  const directive =
    words.length > 10
      ? `text=${encFrag(words.slice(0, 5).join(" "))},${encFrag(words.slice(-5).join(" "))}`
      : `text=${encFrag(quote.trim())}`;
  return url.includes("#") ? `${url}:~:${directive}` : `${url}#:~:${directive}`;
}

/** Label for a citation: web → second-level domain; otherwise the title; truncated to 20 chars. */
function citationLabel(src: { title: string; url: string }, isWeb: boolean): string {
  const raw = isWeb ? secondLevelDomain(src.url) : src.title || src.url;
  return raw.length > 20 ? raw.slice(0, 20) + "…" : raw;
}

/** Icon for a citation button: site favicon for web URLs, a (text-)file icon otherwise. */
function citationIcon(url: string, isWeb: boolean): HTMLElement {
  if (isWeb) {
    const img = document.createElement("img");
    img.className = "md-citation__icon";
    img.width = 12;
    img.height = 12;
    img.alt = "";
    img.src = `https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(url)}`;
    img.addEventListener("error", () => {
      img.style.display = "none";
    });
    return img;
  }
  const span = document.createElement("span");
  span.className = "md-citation__icon md-citation__icon--doc";
  span.innerHTML =
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>';
  return span;
}

// Inline citation marker: [n] or [n:exact quote]. Only the quoted form becomes a button.
const CITATION_RE = /\[(\d+)(?::([^\]]*))?\]/;
const CITATION_RE_G = /\[(\d+)(?::([^\]]*))?\]/g;

/**
 * Replaces inline citation markers [n] or [n:exact quote] with a clickable source button
 * (labelled by source name; web → second-level domain). For web sources the click opens
 * the URL; with a verbatim quote it appends a Scroll-To-Text-Fragment so the browser jumps
 * to/highlights the passage. A marker without a matching source is left as plain text.
 * Skips code/links/buttons. Only affects inline text, not the source listing.
 */
function replaceCitations(root: HTMLElement, citations: SearchSource[]): void {
  if (citations.length === 0 || !root.textContent || !CITATION_RE.test(root.textContent)) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node): number {
      let p = node.parentElement;
      while (p && p !== root) {
        const t = p.tagName;
        if (t === "CODE" || t === "PRE" || t === "A" || t === "BUTTON") return NodeFilter.FILTER_REJECT;
        p = p.parentElement;
      }
      return CITATION_RE.test(node.nodeValue ?? "") ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });
  const targets: Text[] = [];
  while (walker.nextNode()) targets.push(walker.currentNode as Text);

  for (const textNode of targets) {
    const text = textNode.nodeValue ?? "";
    const frag = document.createDocumentFragment();
    const re = new RegExp(CITATION_RE_G);
    let last = 0;
    let replacedAny = false;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const src = citations[parseInt(m[1], 10) - 1];
      // Strip wrapping quote characters the model often adds ([1:"…"] / [1:„…“]) so the
      // text fragment matches the actual page content.
      const quote = (m[2] ?? "").trim().replace(/^["'„“”«»]+|["'„“”«»]+$/g, "").trim();
      frag.appendChild(document.createTextNode(text.slice(last, m.index)));
      last = m.index + m[0].length;
      // No matching source → leave the marker as plain text. Otherwise render a button;
      // a verbatim quote (preferred form) additionally deep-links into the page.
      if (!src) {
        frag.appendChild(document.createTextNode(m[0]));
        continue;
      }
      const isWeb = /^https?:/i.test(src.url);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "md-citation";
      if (quote) btn.title = quote;
      btn.setAttribute("aria-label", `Quelle anzeigen: ${src.title || src.url}${quote ? ` — „${quote}“` : ""}`);
      btn.appendChild(citationIcon(src.url, isWeb));
      const labelEl = document.createElement("span");
      labelEl.className = "md-citation__label";
      labelEl.textContent = citationLabel(src, isWeb);
      btn.appendChild(labelEl);
      if (isWeb) {
        const target = quote ? textFragmentUrl(src.url, quote) : src.url;
        btn.addEventListener("click", () => window.open(target, "_blank", "noopener"));
      }
      frag.appendChild(btn);
      replacedAny = true;
    }
    if (!replacedAny) continue;
    frag.appendChild(document.createTextNode(text.slice(last)));
    textNode.parentNode?.replaceChild(frag, textNode);
  }
}

/**
 * Hängt an jede gerenderte Tabelle einen Copy-Button (oben rechts, nur bei Hover
 * sichtbar), der die Tabelle als Rich-Text ins Clipboard schreibt. Läuft per DOM,
 * da der Markdown-Inhalt via dangerouslySetInnerHTML gerendert wird.
 */
/** Wenn ALLE Zellen der ersten Spalte < 10 Zeichen haben → erste Spalte nicht umbrechen. */
function maybeNowrapFirstColumn(table: HTMLTableElement): void {
  const firstCells = Array.from(table.querySelectorAll("tr"))
    .map((r) => r.querySelector("th,td"))
    .filter((c): c is HTMLTableCellElement => !!c);
  if (firstCells.length === 0) return;
  if (firstCells.every((c) => (c.textContent ?? "").trim().length < 10)) {
    table.classList.add("md-table--nowrap-1");
  }
}

function enhanceTables(root: HTMLElement): void {
  const tables = Array.from(root.querySelectorAll("table"));
  for (const table of tables) {
    if (table.parentElement?.classList.contains("md-table-wrap")) continue;

    maybeNowrapFirstColumn(table as HTMLTableElement);

    const wrap = document.createElement("div");
    wrap.className = "md-table-wrap";
    table.parentNode?.insertBefore(wrap, table);
    wrap.appendChild(table);

    // A full-height rail (absolute, spans the table) holds a position:sticky button so
    // it stays pinned to the top of the visible area while scrolling — and is bounded
    // by the table, so it scrolls out only once the table is nearly gone.
    const rail = document.createElement("div");
    rail.className = "md-table-rail";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "md-table-copy";
    btn.setAttribute("aria-label", "Tabelle kopieren");
    btn.title = "Tabelle kopieren";
    btn.innerHTML = COPY_ICON_SVG;
    btn.addEventListener("click", () => {
      void copyTableToClipboard(table as HTMLTableElement).then(() => {
        btn.classList.add("md-table-copy--copied");
        window.setTimeout(() => btn.classList.remove("md-table-copy--copied"), 1500);
      });
    });
    rail.appendChild(btn);
    wrap.appendChild(rail);
  }
}

/**
 * Inline-Code (`<code>` außerhalb von `<pre>`) wird klickbar: Cursor-Pointer + Klick kopiert
 * den Inhalt in die Zwischenablage (kurzes Feedback). Idempotent (mehrfaches Enhancen sicher).
 */
function enhanceInlineCode(root: HTMLElement): void {
  const codes = Array.from(root.querySelectorAll("code"));
  for (const code of codes) {
    if (code.closest("pre")) continue; // Block-Code (eigene Behandlung), nicht inline
    if (code.classList.contains("md-inline-code")) continue;
    code.classList.add("md-inline-code");
    code.setAttribute("title", "Klicken zum Kopieren");
    code.addEventListener("click", () => {
      const text = code.textContent ?? "";
      if (!text) return;
      void writeRichText(`<code>${text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</code>`, text)
        .then(() => {
          code.classList.add("md-inline-code--copied");
          window.setTimeout(() => code.classList.remove("md-inline-code--copied"), 1000);
        })
        .catch(() => {
          /* clipboard not available */
        });
    });
  }
}

/**
 * Ersetzt Code-Blöcke (`<pre><code>`) durch read-only CodeMirror-Ansichten (Syntax-Highlighting,
 * Zeilennummern, Sprach-Label + Copy-Button). Liefert die erzeugten Views zurück, damit der
 * Aufrufer sie beim nächsten Render / Unmount zerstören kann. Block-Code bekommt KEIN inline-copy.
 */
const CHEVRON_SVG =
  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';

function enhanceCodeBlocks(root: HTMLElement): EditorView[] {
  const views: EditorView[] = [];
  for (const pre of Array.from(root.querySelectorAll("pre"))) {
    const codeEl = pre.querySelector("code");
    if (!codeEl) continue;
    const code = (codeEl.textContent ?? "").replace(/\n$/, ""); // marked hängt ein \n an
    const langClass = Array.from(codeEl.classList).find((c) => c.startsWith("language-"));
    const lang = langClass ? langClass.slice("language-".length) : "";
    const lineCount = code === "" ? 0 : code.split("\n").length;

    const block = document.createElement("div");
    block.className = "cm-block";

    const bar = document.createElement("div");
    bar.className = "cm-block__bar";
    const label = document.createElement("span");
    label.className = "cm-block__lang";
    label.textContent = lang || "code";
    bar.appendChild(label);

    const actions = document.createElement("div");
    actions.className = "cm-block__actions";

    // Auf-/Zuklappen (Label beschreibt die Aktion; Chevron rotiert)
    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "cm-block__action cm-block__toggle";
    const toggleLabel = document.createElement("span");
    toggle.innerHTML = CHEVRON_SVG;
    toggle.appendChild(toggleLabel);

    const copy = document.createElement("button");
    copy.type = "button";
    copy.className = "cm-block__action cm-block__copy";
    copy.title = "Code kopieren";
    const copyLabel = document.createElement("span");
    copyLabel.textContent = "Kopieren";
    copy.innerHTML = COPY_ICON_SVG;
    copy.appendChild(copyLabel);
    copy.addEventListener("click", () => {
      const esc = code.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      void writeRichText(`<pre><code>${esc}</code></pre>`, code)
        .then(() => {
          copy.classList.add("cm-block__copy--copied");
          window.setTimeout(() => copy.classList.remove("cm-block__copy--copied"), 1500);
        })
        .catch(() => {
          /* clipboard not available */
        });
    });

    actions.appendChild(toggle);
    actions.appendChild(copy);
    bar.appendChild(actions);

    const editorHost = document.createElement("div");
    editorHost.className = "cm-block__editor";

    const collapsed = document.createElement("div");
    collapsed.className = "cm-block__collapsed";
    collapsed.textContent = `${lineCount} ausgeblendete Zeile${lineCount !== 1 ? "n" : ""}`;

    block.appendChild(bar);
    block.appendChild(editorHost);
    block.appendChild(collapsed);
    pre.replaceWith(block);

    const view = mountCodeBlock(editorHost, code, lang);
    views.push(view);

    const setCollapsed = (isCollapsed: boolean): void => {
      block.classList.toggle("cm-block--collapsed", isCollapsed);
      toggleLabel.textContent = isCollapsed ? "Ausklappen" : "Einklappen";
      toggle.setAttribute("aria-expanded", String(!isCollapsed));
      if (!isCollapsed) view.requestMeasure(); // nach dem Einblenden neu vermessen
    };
    setCollapsed(false); // standardmäßig aufgeklappt
    toggle.addEventListener("click", () => setCollapsed(!block.classList.contains("cm-block--collapsed")));
  }
  return views;
}

/** Rendert Markdown-Inhalt als sanitisiertes HTML (siehe services/markdown). */
export function Markdown({ content, className, citations, enhanceCode = true }: MarkdownProps): JSX.Element {
  const ref = React.useRef<HTMLDivElement>(null);
  const html = React.useMemo(() => renderMarkdown(content), [content]);
  const citationKey = (citations ?? []).map((c) => c.title || c.url).join("|");

  React.useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    enhanceTables(root);
    enhanceInlineCode(root);
    const views = enhanceCode ? enhanceCodeBlocks(root) : [];
    if (citations && citations.length > 0) replaceCitations(root, citations);
    return () => {
      for (const v of views) v.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, citationKey, enhanceCode]);

  return <div ref={ref} className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}
